import React from "react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import Seo from "@/components/Seo";
import { ScheduleCallButton } from "@/components/ScheduleCallButton";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { HOME } from "@/lib/homeContent";

const K = { fontFamily: "Kanit, Arial, sans-serif" };
const S = { fontFamily: "'Noto Serif', Georgia, serif" };
const KARIN = "/img/karin-eckhard-portrait.jpg";

export default function About() {
  const { section } = useSiteContent();
  const c = section("about", HOME.about);

  return (
    <main data-testid="about-page" className="min-h-screen bg-[#F5EFE7] text-[#451C25]">
      <Seo title="About Destino Europe | A team on both sides of your move" description={c.sub} path="/about" />
      <Header />

      {/* 1: Hero: people first */}
      <section className="bg-[#183129] text-white pt-40 pb-20 md:pb-28 px-6 md:px-10 lg:px-14">
        <div className="max-w-5xl mx-auto">
          <p className="text-[#B3A17D] text-[11px] tracking-[0.3em] uppercase mb-6 font-semibold" style={K}>{c.eyebrow}</p>
          <h1 data-testid="about-headline" className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl" style={{ ...K, fontWeight: 600 }}>{c.headline}</h1>
          <p className="mt-8 max-w-2xl text-white/85 text-lg md:text-xl leading-relaxed" style={S}>{c.sub}</p>
        </div>
      </section>

      {/* 2: Karin / the origin (real photo, editorial layout) */}
      <section data-testid="about-origin" className="py-20 md:py-28 px-6 md:px-10 lg:px-14">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <figure className="lg:col-span-5">
            <img
              data-testid="about-karin-photo"
              src={KARIN}
              alt="Karin Eckhard"
              loading="eager"
              className="w-full aspect-[4/5] object-cover"
            />
            <figcaption className="mt-3 text-[11px] tracking-[0.08em] text-[#897756]" style={K}>{c.origin_caption}</figcaption>
          </figure>
          <div className="lg:col-span-7">
            <h2 data-testid="about-origin-headline" className="text-3xl md:text-4xl leading-[1.1] tracking-tight" style={{ ...K, fontWeight: 600 }}>{c.origin_headline}</h2>
            <div className="mt-7 space-y-5 text-[#451C25] text-base md:text-lg leading-relaxed" style={S}>
              <p data-testid="about-origin-p1">{c.origin_p1}</p>
              <p>{c.origin_p2}</p>
              <p>{c.origin_p3}</p>
              <p className="text-[#6B4954]">{c.origin_p4}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 3: USA + Spain */}
      <section data-testid="about-team" className="bg-white/60 py-20 md:py-24 px-6 md:px-10 lg:px-14">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <h2 className="text-3xl md:text-4xl leading-[1.1] tracking-tight" style={{ ...K, fontWeight: 600 }}>{c.team_headline}</h2>
          </div>
          <div className="md:col-span-7 space-y-5 text-base md:text-lg leading-relaxed text-[#451C25]" style={S}>
            <p>{c.team_p1}</p>
            <p>{c.team_p2}</p>
            <p className="text-[#6B4954]">{c.team_p3}</p>
          </div>
        </div>
      </section>

      {/* 4: Local knowledge + CTA */}
      <section data-testid="about-local" className="bg-[#451C25] text-white py-20 md:py-28 px-6 md:px-10 lg:px-14">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <h2 data-testid="about-local-headline" className="text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight" style={{ ...K, fontWeight: 600 }}>{c.local_headline}</h2>
            <div className="mt-8"><ScheduleCallButton label={c.cta} source="about" tone="sand" testId="about-cta" /></div>
            <p className="mt-3 text-white/60 text-[11px] tracking-[0.14em] uppercase">Free. No obligation.</p>
          </div>
          <div className="md:col-span-7 space-y-5 text-white/85 text-base md:text-lg leading-relaxed" style={S}>
            <p>{c.local_p1}</p>
            <p>{c.local_p2}</p>
            <p className="text-[#B3A17D]">{c.local_p3}</p>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
